import { defineStore } from 'pinia'
import { globalApi } from '@/api/global'
import { isNumber, isString } from 'lodash-es'
import { localReference } from '@/types/local-reference'

const referenceColorMap = {
  green: '#00B42A',
  orange: '#FF9A2E',
  red: '#F53F3F',
  blue: '#0076f6'
}

interface GlobalState {

  /** 枚举类型 */
  reference: Nullable<
    Partial<{ [key in Reference.ReferenceKey]: Reference.ReferenceItem[] }>
  >;
  referenceColorMap: typeof referenceColorMap;
}

/** app 全局信息 */
export const useAppStore = defineStore('App', {
  state: (): GlobalState => ({
    reference: null,

    /** reference 中配置的颜色转换器 */
    referenceColorMap,
  }),

  actions: {

    /** 获取枚举值详情 */
    getReferenceLabel(referenceKey: Reference.ReferenceKey, value: number): string {
      const referenceItem = this.getReferenceItems(referenceKey)
      if (!isNumber(value) && !isString(value)) {
        return ''
      }
      return (
        referenceItem.find(item => item.value.toString() === value.toString())
          ?.label || ''
      )
    },

    /** 获取枚举值列表 */
    getReferenceItems(referenceKey: Reference.ReferenceKey): Reference.ReferenceItem[] {
      return this.reference?.[referenceKey] || []
    },

    /** 获取枚举值的值映射列表 */
    getReferenceItemsValueMap(referenceKey: Reference.ReferenceKey): Record<number, Reference.ReferenceItem> {
      let valueMap: Record<number, Reference.ReferenceItem> = {}
      this.getReferenceItems(referenceKey).forEach(item => {
        valueMap[item.value] = toRaw(item)
      })
      return valueMap
    },

    /** 获取枚举值的值映射列表 */
    getReferenceItemsLabelMap(referenceKey: Reference.ReferenceKey): Record<string, Reference.ReferenceItem> {
      let labelMap: Record<string, Reference.ReferenceItem> = {}
      this.getReferenceItems(referenceKey).forEach(item => {
        labelMap[item.label] = toRaw(item)
      })
      return labelMap
    },

    /** 获取枚举值常量 map */
    getReferenceConstantMap<T extends Reference.ReferenceKey>(referenceKey: T): Reference.ConstantKeyMap[T] {
      let constantMap = {}
      this.getReferenceItems(referenceKey).forEach(item => {
        constantMap[item.key!] = toRaw(item)
      })
      return constantMap as Reference.ConstantKeyMap[T]
    },

    /** 重置枚举值 */
    async refreshReference() {
      this.reference = await globalApi.getReference()
      this.reference = Object.assign(localReference, this.reference)

      const weappVersionAuditStatusEnum = this.getReferenceConstantMap('weappVersionAuditStatusEnum')
      weappVersionAuditStatusEnum.AUDIT_PASS.color = 'green'
      weappVersionAuditStatusEnum.AUDITING.color = 'orange'
      weappVersionAuditStatusEnum.AUDIT_REJECT.color = 'red'
      weappVersionAuditStatusEnum.UN_AUDIT.color = 'blue'

      const weappAuthStatusEnum = this.getReferenceConstantMap('weappAuthStatusEnum')
      weappAuthStatusEnum.AUTHED.color = 'green'
      weappAuthStatusEnum.UN_AUTH.color = 'orange'
      weappAuthStatusEnum.CANCEL_AUTH.color = 'red'
    },
  },
})
